// Location: src/components/admin/AIManagement.tsx
// Purpose: AI management tools for image analysis and chat completion
// Scope: Shows AI tools for image analysis and chat completion

/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
import { useAction } from 'convex/react';
import { api } from '@/convex/_generated/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { useMutation, useQuery } from 'convex/react';
import { Id } from '@/convex/_generated/dataModel';

export function AIManagement() {
	const { toast } = useToast();
	const [imageUrl, setImageUrl] = React.useState('');
	const [imagePrompt, setImagePrompt] = React.useState('');
	const [chatPrompt, setChatPrompt] = React.useState('');
	const [systemPrompt, setSystemPrompt] = React.useState('');
	const [isAnalyzing, setIsAnalyzing] = React.useState(false);
	const [isChatting, setIsChatting] = React.useState(false);
	const [error, setError] = React.useState<string | null>(null);
	const [result, setResult] = React.useState<string | null>(null);
	const analyzeImageAction = useAction(api.ai.analyzeImage);
	const chatCompletionAction = useAction(api.ai.chatCompletion);

	const handleImageAnalysis = async () => {
		try {
			setIsAnalyzing(true);
			setError(null);
			setResult(null);

			if (!imageUrl) {
				throw new Error('Image URL is required');
			}

			const response = await analyzeImageAction({
				imageUrl,
				prompt: imagePrompt || undefined,
			});

			if (response?.success) {
				setResult(response.analysis as string);
				toast({
					title: '✨ Analysis Complete',
					description: 'Image successfully analyzed',
					duration: 3000,
				});
			}
		} catch (error) {
			setError(
				error instanceof Error ? error.message : 'Analysis failed'
			);
			toast({
				title: '🔥 Error',
				description:
					error instanceof Error ? error.message : 'Analysis failed',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsAnalyzing(false);
		}
	};

	const handleChatCompletion = async () => {
		try {
			setIsChatting(true);
			setError(null);
			setResult(null);

			if (!chatPrompt) {
				throw new Error('Chat prompt is required');
			}

			const response = await chatCompletionAction({
				prompt: chatPrompt,
				systemPrompt: systemPrompt || undefined,
			});

			if (response?.success) {
				const responseText =
					typeof response.response === 'string'
						? response.response
						: JSON.stringify(response.response);

				setResult(responseText);
				toast({
					title: '✨ Chat Complete',
					description: 'Response generated successfully',
					duration: 3000,
				});
			}
		} catch (error) {
			setError(error instanceof Error ? error.message : 'Chat failed');
			toast({
				title: '🔥 Error',
				description:
					error instanceof Error ? error.message : 'Chat failed',
				variant: 'destructive',
				duration: 3000,
			});
		} finally {
			setIsChatting(false);
		}
	};

	return (
		<div className='container mx-auto p-6'>
			<h1 className='text-3xl font-bold mb-2 pt-8'>AI Tools</h1>
			<p className='text-lg text-gray-500 mb-6 pb-8'>
				Monitor AI operations, analyze images, and test chat completions
			</p>

			<div className='space-y-8'>
				{/* Image Analysis Card */}
				<Card className='glow-card'>
					<CardHeader>
						<CardTitle className='text-3xl font-bold bg-gradient-to-r from-pink-400 to-pink-600 text-transparent bg-clip-text'>
							Image Analysis
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-4 mb-12'>
							<Input
								placeholder='Image URL'
								value={imageUrl}
								onChange={(e) => setImageUrl(e.target.value)}
							/>
							<Textarea
								placeholder='Optional analysis prompt'
								value={imagePrompt}
								onChange={(e) => setImagePrompt(e.target.value)}
							/>
							<Button
								onClick={() => void handleImageAnalysis()}
								disabled={isAnalyzing}
								className='w-full my-8'
							>
								{isAnalyzing
									? '🔄 Analyzing...'
									: '🔍 Analyze Image'}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Chat Completion Card */}
				<Card className='glow-card mb-24'>
					<CardHeader>
						<CardTitle className='text-3xl font-bold bg-gradient-to-r from-pink-400 to-pink-600 text-transparent bg-clip-text'>
							Chat Completion
						</CardTitle>
					</CardHeader>
					<CardContent>
						<div className='space-y-4 mb-12'>
							<Textarea
								placeholder='System prompt (optional)'
								value={systemPrompt}
								onChange={(e) =>
									setSystemPrompt(e.target.value)
								}
							/>
							<Textarea
								placeholder='Chat prompt'
								value={chatPrompt}
								onChange={(e) => setChatPrompt(e.target.value)}
							/>
							<Button
								onClick={() => void handleChatCompletion()}
								disabled={isChatting}
								className='w-full'
							>
								{isChatting
									? '🔄 Generating...'
									: '💭 Generate Response'}
							</Button>
						</div>
					</CardContent>
				</Card>

				{/* Results Card */}
				{(result || error) && (
					<Card>
						<CardHeader>
							<CardTitle
								className={
									error ? 'text-red-500' : 'text-green-500'
								}
							>
								{error ? '🔥 Error' : '✨ Result'}
							</CardTitle>
						</CardHeader>
						<CardContent>
							{error ? (
								<Alert variant='destructive'>
									<AlertTitle>Error</AlertTitle>
									<AlertDescription>{error}</AlertDescription>
								</Alert>
							) : (
								<div className='whitespace-pre-wrap'>
									{result}
								</div>
							)}
						</CardContent>
					</Card>
				)}
			</div>
		</div>
	);
}
